'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { LocaleConfig, LOCALE_PRESETS, getActiveLocale, saveLocaleToStorage, localiseMoneyString, localiseAmount } from './localisation';
import { createClient } from '@/lib/supabase/client';

interface LocaleContextValue {
  locale: LocaleConfig | null;
  setLocale: (countryCode: string) => void;
  /** Replace £ amounts in a string with the user's local currency */
  localiseMoney: (text: string) => string;
  /** Convert a GBP amount to the user's currency and format it */
  formatMoney: (gbpAmount: number, options?: { decimals?: boolean }) => string;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: null,
  setLocale: () => {},
  localiseMoney: (t) => t,
  formatMoney: (n) => `$${n}`,
});

/**
 * User-scoped locale storage key.
 * Prevents User A's locale from leaking to User B in the same browser.
 */
function localeStorageKey(userId: string | null): string {
  return userId ? `cs_locale_${userId}` : 'cs_locale_anon';
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleConfig | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Resolve the current authenticated user to scope locale storage
    const supabase = createClient();

    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      loadLocaleForUser(uid);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      loadLocaleForUser(uid);
    });

    return () => subscription.unsubscribe();
  }, []);

  function loadLocaleForUser(uid: string | null) {
    if (typeof window === 'undefined') return;
    try {
      const key = localeStorageKey(uid);
      const stored = localStorage.getItem(key);
      if (stored && LOCALE_PRESETS[stored]) {
        setLocaleState(LOCALE_PRESETS[stored]);
      } else {
        // Fall back to browser locale detection (no user-specific default)
        const active = getActiveLocale();
        setLocaleState(active);
      }
    } catch {
      setLocaleState(null);
    }
  }

  const setLocale = (countryCode: string) => {
    const config = LOCALE_PRESETS[countryCode];
    if (config) {
      setLocaleState(config);
      // Save under user-scoped key
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(localeStorageKey(userId), countryCode);
        } catch {}
      }
      // Also call the original helper for any other consumers
      saveLocaleToStorage(countryCode);
    }
  };

  const localiseMoney = (text: string) =>
    locale ? localiseMoneyString(text, locale) : text;

  const formatMoney = (gbpAmount: number, options?: { decimals?: boolean }) =>
    locale ? localiseAmount(gbpAmount, locale, options) : `$${gbpAmount}`;

  return (
    <LocaleContext.Provider value={{ locale, setLocale, localiseMoney, formatMoney }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
